import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, getErrorMessage } from "@/lib/api";
import { Loader2, CheckCircle, XCircle, Globe, Lock, Sparkles } from "lucide-react";

export default function WordPressAuth() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const siteUrl = params.get("site_url") || "";
    const redirectUri = params.get("redirect_uri") || "";
    const state = params.get("state") || "";
    const labelParam = params.get("label") || "";

    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");
    const authDoneRef = useRef(false);

    const siteDisplay = siteUrl.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

    useEffect(() => {
        if (!authLoading && !user) {
            // Not logged in → send to login, preserve this URL
            navigate(`/login?next=${encodeURIComponent(window.location.href)}`, { replace: true });
        }
    }, [user, authLoading, navigate]);

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream-50">
                <Loader2 className="animate-spin text-coral" size={32} />
            </div>
        );
    }

    if (!siteUrl || !redirectUri || !state) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream-50 p-5">
                <div className="bg-white rounded-3xl border border-cream-300 shadow-soft p-8 max-w-sm w-full text-center">
                    <XCircle className="mx-auto text-coral mb-3" size={40} />
                    <h1 className="font-display text-[22px] font-bold mb-2">Invalid request</h1>
                    <p className="text-[14px] text-ink-muted">Missing required parameters. Please try connecting again from your WordPress plugin settings.</p>
                </div>
            </div>
        );
    }

    const handleAuthorize = async () => {
        if (authDoneRef.current) return;
        authDoneRef.current = true;
        setStatus("loading");
        try {
            const { data } = await api.post("/auth/wp/authorize", {
                site_url: siteUrl,
                redirect_uri: redirectUri,
                state,
                label: labelParam || siteUrl,
            });
            setStatus("success");
            // Redirect WordPress plugin back with the code
            const callbackUrl = new URL(redirectUri);
            callbackUrl.searchParams.set("code", data.code);
            callbackUrl.searchParams.set("state", state);
            setTimeout(() => {
                window.location.href = callbackUrl.toString();
            }, 1200);
        } catch (err) {
            authDoneRef.current = false;
            setStatus("error");
            setError(getErrorMessage(err));
        }
    };

    const handleDeny = () => {
        const callbackUrl = new URL(redirectUri);
        callbackUrl.searchParams.set("error", "access_denied");
        callbackUrl.searchParams.set("state", state);
        window.location.href = callbackUrl.toString();
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-cream-50 p-5">
            <div className="bg-white rounded-3xl border border-cream-300 shadow-soft p-8 max-w-md w-full">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-2xl bg-coral flex items-center justify-center">
                        <Sparkles size={18} className="text-white" />
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-ink-muted">Audo.dev</p>
                        <h1 className="font-display text-[20px] font-bold leading-tight">WordPress Authorization</h1>
                    </div>
                </div>

                {status === "success" ? (
                    <div className="text-center py-4">
                        <CheckCircle className="mx-auto text-emerald-500 mb-3" size={40} />
                        <p className="font-semibold text-[16px]">Authorized!</p>
                        <p className="text-[13px] text-ink-muted mt-1">Redirecting back to your WordPress site…</p>
                    </div>
                ) : (
                    <>
                        {/* Site info */}
                        <div className="bg-cream-100 rounded-2xl p-4 mb-5 flex items-center gap-3">
                            <Globe size={18} className="text-coral flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-[12px] text-ink-muted">Requesting access from</p>
                                <p className="font-semibold text-[15px] truncate">{siteDisplay}</p>
                            </div>
                        </div>

                        {/* Account info */}
                        <div className="mb-5">
                            <p className="text-[13px] text-ink-muted mb-2">You are authorizing as:</p>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-ink flex items-center justify-center text-cream-100 font-bold text-[14px]">
                                    {(user?.name || user?.email || "U")[0].toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-semibold text-[14px]">{user?.name}</p>
                                    <p className="text-[12px] text-ink-muted">{user?.email} · <span className="capitalize">{user?.plan || "free"} plan</span></p>
                                </div>
                            </div>
                        </div>

                        {/* Permissions */}
                        <div className="bg-cream-50 rounded-2xl p-4 mb-5 space-y-2">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-2">This will allow the plugin to:</p>
                            {[
                                "Generate audio from your post content",
                                "Use your plan's TTS credits",
                                "Read your available voices",
                            ].map((perm) => (
                                <div key={perm} className="flex items-center gap-2">
                                    <Lock size={12} className="text-emerald-500 flex-shrink-0" />
                                    <span className="text-[13px]">{perm}</span>
                                </div>
                            ))}
                        </div>

                        {error && (
                            <div className="mb-4 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-[13px] text-red-700">
                                {error}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={handleDeny}
                                disabled={status === "loading"}
                                className="flex-1 border border-cream-300 hover:bg-cream-100 text-ink rounded-full py-3 text-[14px] font-semibold transition disabled:opacity-50"
                            >
                                Deny
                            </button>
                            <button
                                onClick={handleAuthorize}
                                disabled={status === "loading"}
                                className="flex-1 bg-coral hover:bg-ink text-white rounded-full py-3 text-[14px] font-semibold transition inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {status === "loading" ? (
                                    <><Loader2 size={14} className="animate-spin" /> Authorizing…</>
                                ) : (
                                    "Authorize"
                                )}
                            </button>
                        </div>
                        <p className="text-[11px] text-ink-muted text-center mt-4">
                            You can revoke this access at any time from <strong>Settings → API keys</strong>.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
